import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import API from '@/lib/api'
import { toggleFeatureIds } from '@/lib/features/featureList'
import { AdminAccountDetail, AdminAccountFeature } from '@/types/Account'

const schema = z.object({
  features: z.array(z.number()),
})

type AdminAccountFeaturesFormData = z.infer<typeof schema>

export function AdminAccountFeaturesForm({
  accountId,
  features,
  onSuccess,
}: {
  accountId: string
  features: AdminAccountFeature[]
  onSuccess: (account: AdminAccountDetail) => void
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'admin.accounts.detail.features',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  const form = useForm<AdminAccountFeaturesFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      features: features.filter((feature) => feature.enabled).map((feature) => feature.id),
    },
  })

  const onSubmit = (data: AdminAccountFeaturesFormData) => {
    API.patch(`/api/v2/admin/accounts/${accountId}`, {
      features: data.features,
    })
      .then((response) => {
        toast.success(t('success'))
        onSuccess(response.data)
      })
      .catch(() => {
        toast.error(t('error'))
      })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {features.map((feature) => (
          <FormField
            key={feature.id}
            control={form.control}
            name="features"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value.includes(feature.id)}
                    onCheckedChange={(checked) => {
                      field.onChange(
                        toggleFeatureIds(
                          field.value,
                          feature.id,
                          checked === true,
                          features,
                        ),
                      )
                    }}
                  />
                </FormControl>
                <FormLabel className="font-normal">{feature.name}</FormLabel>
              </FormItem>
            )}
          />
        ))}

        <Button type="submit">{tg('button.save')}</Button>
      </form>
    </Form>
  )
}
